import React, { useState, useEffect } from "react";
import { Typography, Box, Select, MenuItem, FormControl } from "@mui/material";
import "./CardPieces.scss";

const DropdownCard = ({ 
    pickState, 
    setPickState, 
    isPropSelected, 
    interval, 
    hasGameStarted, 
    isGameFinal, 
    prop, 
    selectedGameId, 
    result,
    customBorderColor, 
    canUserMakePick = true, // Default to true if not provided
    setRestrictedModalOpen,
}) => {
    let id = prop?.id;
    const globalGameId = selectedGameId;
    const propChoice = prop?.propChoice;
    const options = prop?.options || [];
    const line = prop?.line;
    const correctOption = prop?.correctOption;
    const correctOptions = prop?.correctOptions || []; // New prop for multiple correct answers
    const actualTotal = prop?.actualTotal;
    console.log("Correct Option:", correctOption, "Correct Options:", correctOptions, "Line:", line, "Actual Total:", actualTotal);

    const [selectedOption, setSelectedOption] = useState('');
    console.log("Selected Option:", selectedOption, "Line:", line);

    const colors = {
        loss: "#CC293C",
        lossBackground: "#CC293C33",
        win: "#00AA72",
        winBackgroundColor: "#CCFFEF",
        pendingNotLocked: "#00AAD1",
        pendingNotLockedBackground: "#00AAD133",
        pendingLocked: "#002129",
        pendingLockedBackground: "#00212933",
        notSelected: "#00212980",
        winNotSelected: "#00AA72",
        defaultBackground: "#fff",
        textColor: "#002129",
        voidedBackground: "#000000",
        voidedText: "#fff",
    };

    const isCorrectOption = (option) => {
        if (correctOption === 'Multiple Answers') {
            return correctOptions.includes(option);
        }
        return correctOption === option;
    };

    const getDropdownStyles = () => {
        const isCorrect = isCorrectOption(selectedOption);
        let styles = {
            borderColor: colors.notSelected,
            backgroundColor: colors.defaultBackground,
            textColor: colors.textColor,
        };
    
        if (correctOption === 'Void') {
            styles = {
                borderColor: colors.notSelected,
                backgroundColor: colors.voidedBackground,
                textColor: colors.voidedText,
            };
        } else if (selectedOption && !hasGameStarted) {
            styles = {
                borderColor: colors.pendingNotLocked,
                backgroundColor: colors.pendingNotLockedBackground,
                textColor: colors.textColor,
            };
        } else if (selectedOption && hasGameStarted && result === "Pending") {
            styles = {
                borderColor: colors.pendingLocked,
                backgroundColor: colors.pendingLockedBackground,
                textColor: colors.textColor,
            };
        } else if (selectedOption && result !== "Pending") {
            styles = {
                textColor: colors.textColor,
                borderColor: (result === "Win" || result === "win") ? colors.win : colors.loss,
                backgroundColor: (result === "Win" || result === "win") ? colors.winBackgroundColor : colors.lossBackground,
            };
        } else if (!selectedOption && hasGameStarted && correctOption && correctOption !== 'Void') {
            // Show red styling when no selection was made and game has started (like a wrong pick)
            styles = {
                borderColor: colors.loss,
                backgroundColor: colors.lossBackground,
                textColor: colors.textColor,
            };
        } else if (!selectedOption) {
            styles = {
                borderColor: '#002129',
                backgroundColor: '#fff',
                textColor: '#002129',
            };
        }
    
        return styles;
    };

    useEffect(() => {
        const existingPick = pickState?.find(pick => 
            pick.gameId === globalGameId && pick.id === id
        );
        setSelectedOption(existingPick?.option || '');
    }, [pickState, globalGameId, id]);

    const handleChange = (event) => {
        const option = event.target.value;
        
        const newPick = {
            id: id,
            propChoice: propChoice,
            option: option,
            gameId: globalGameId,
            interval: Number(interval),
        };
     
        const existingPickIndex = pickState?.findIndex(
            (pick) => pick.propChoice === propChoice && pick.gameId === globalGameId && pick.id === id
        );
     
        if (option === '') {
            // User selected "Select Answer" - remove existing pick if any
            if (existingPickIndex !== -1) {
                const updatedPicks = pickState.filter((_, idx) => idx !== existingPickIndex);
                setPickState(updatedPicks);
            }
            setSelectedOption('');
        } else {
            if (existingPickIndex !== -1) {
                // Update existing pick
                const updatedPicks = [...pickState];
                updatedPicks[existingPickIndex] = newPick;
                setPickState(updatedPicks);
            } else {
                // This is a NEW pick - check if user can add it
                if (!canUserMakePick) {
                    setRestrictedModalOpen(true);
                    return;
                }
                // Adding new pick
                setPickState([...pickState, newPick]);
            }
            setSelectedOption(option);
        }
    };

    const styles = getDropdownStyles();

    return (
        <Box 
            className="quick-pick-card-dropdown-container"
            sx={{
                borderRight: customBorderColor ? `1px solid ${customBorderColor}` : '1px solid #002129',
                borderLeft: customBorderColor ? `1px solid ${customBorderColor}` : '1px solid #002129',
                padding: '12px 0',
            }} 
        >
            {hasGameStarted ? (
                <>
                    <Box 
                        sx={{
                            border: `2px solid ${styles.borderColor}`,
                            width: '90%',
                            margin: '0 auto',
                            backgroundColor: styles.backgroundColor,
                            padding: '12px 16px',
                            borderRadius: '4px',
                            minHeight: '56px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography
                            sx={{ 
                                color: styles.textColor,
                                fontSize: '16px',
                                textAlign: 'center',
                            }}
                        >
                            {selectedOption || 'No Selection'}
                        </Typography>
                    </Box>
                    {!selectedOption && (correctOption || correctOptions) && actualTotal && (
                        <Typography
                            sx={{ 
                                color: styles.textColor,
                                fontSize: '16px',
                                marginTop: '8px',
                                fontWeight: '700',
                            }}
                        >
                            {correctOption || correctOptions.length > 0 ?
                                `Correct Answer: ${correctOption || correctOptions.join(', ')}${line ? ` (${line})` : ''}`
                                : `Actual Total: ${actualTotal}${line ? ` (${line})` : ''}`}
                        </Typography>
                    )}
                </>
            ) : (
                <FormControl  
                    disabled={correctOption === 'Void'}
                    sx={{
                        width: '90%',
                        margin: '0 auto',
                        '& .MuiOutlinedInput-root': {
                            border: `2px solid ${styles.borderColor}`,
                            backgroundColor: styles.backgroundColor,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                        },
                        '& .MuiSelect-select': {
                            color: styles.textColor,
                            textAlign: 'center', // Center the text in the select box
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        },
                        '& .MuiSelect-icon': {
                            color: styles.textColor,
                        }
                    }}
                >
                    <Select
                        value={selectedOption}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(value) => {
                            if (!value) {
                                return (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}>
                                        <Typography sx={{ 
                                            color: styles.textColor, 
                                            textAlign: 'center'
                                        }}>
                                            Select Answer
                                        </Typography>
                                    </Box>
                                );
                            }
                            return (
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    width: '100%'
                                }}>
                                    <Typography sx={{ 
                                        color: styles.textColor, 
                                        textAlign: 'center'
                                    }}>
                                        {value}
                                    </Typography>
                                </Box>
                            );
                        }}
                    >
                        <MenuItem value="">
                            <Typography sx={{ width: '100%', textAlign: 'center' }}>
                                Select Answer
                            </Typography>
                        </MenuItem>
                        {options?.map((option, index) => (
                            <MenuItem key={index} value={option}>
                                <Typography sx={{ 
                                    color: styles.textColor, 
                                    width: '100%', 
                                }}>
                                    {option}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
};

export default DropdownCard;